import React from 'react';
import { STATUS_KEY_SELECT_OPTIONS } from '../../constants/projectStatus';
import { LEAD_SOURCES } from '../../constants/leadSources';
import { CLASSIFICATION_KEYS } from '../../constants/classifications';

// Catalog of project + customer fields the user can build conditions on.
// Kept in sync with ConditionEvaluator.GetFieldValue on the backend — adding
// a new field here without the backend (or vice versa) silently no-ops.
export const FIELD_OPTIONS = [
    { group: 'Project', value: 'status', label: 'Status' },
    { group: 'Project', value: 'type', label: 'Type' },
    { group: 'Project', value: 'classification', label: 'Classification' },
    { group: 'Project', value: 'salesRepId', label: 'Sales Rep ID' },
    { group: 'Project', value: 'projectName', label: 'Project Name' },
    { group: 'Project', value: 'poNumber', label: 'PO Number' },
    { group: 'Project', value: 'description', label: 'Description' },
    { group: 'Project', value: 'notes', label: 'Notes' },
    { group: 'Project', value: 'leadSource', label: 'Lead Source' },
    { group: 'Project', value: 'currentInstaller', label: 'Current Installer' },
    { group: 'Project', value: 'changeOrder', label: 'Change Order' },
    { group: 'Project', value: 'isNotRoofRelated', label: 'Is Not Roof Related (NRR)' },

    { group: 'Insurance', value: 'insuranceCarrier', label: 'Insurance Carrier' },
    { group: 'Insurance', value: 'claimNumber', label: 'Claim #' },
    { group: 'Insurance', value: 'dateOfLoss', label: 'Date of Loss' },

    { group: 'Financial', value: 'estimatedCost', label: 'Estimated Cost' },
    { group: 'Financial', value: 'finalCost', label: 'Final Cost' },
    { group: 'Financial', value: 'amountPaid', label: 'Amount Paid' },

    { group: 'Dates', value: 'estimateDate', label: 'Estimate Date' },
    { group: 'Dates', value: 'contractSignedDate', label: 'Contract Signed Date' },
    { group: 'Dates', value: 'scheduledStartDate', label: 'Scheduled Start Date' },
    { group: 'Dates', value: 'completionDate', label: 'Completion Date' },
    { group: 'Dates', value: 'materialDeliveryDate', label: 'Material Delivery Date' },
    { group: 'Dates', value: 'workOrderStartDate', label: 'Work Order Start Date' },

    { group: 'Work Details', value: 'shingleBrand', label: 'Shingle Brand' },
    { group: 'Work Details', value: 'shingleLine', label: 'Shingle Type (product line)' },
    // Backend field name is shingleType; it holds Architectural / 3-Tab / Luxury /
    // Premium, which the rest of the UI now calls Profile.
    { group: 'Work Details', value: 'shingleType', label: 'Shingle Profile' },
    { group: 'Work Details', value: 'shingleColor', label: 'Shingle Color' },
    { group: 'Work Details', value: 'sidingColor', label: 'Siding Color' },
    { group: 'Work Details', value: 'hasMetalWork', label: 'Has Metal Work' },
    { group: 'Work Details', value: 'gutters', label: 'Gutters' },
    { group: 'Work Details', value: 'skylights', label: 'Skylights' },
    { group: 'Work Details', value: 'gutterGuards', label: 'Gutter Guards' },
    { group: 'Work Details', value: 'gutterColor', label: 'Gutter Color' },
    { group: 'Work Details', value: 'gutterFeet', label: 'Gutter Feet' },
    { group: 'Work Details', value: 'downspoutFeet', label: 'Downspout Feet' },
    { group: 'Work Details', value: 'oneStoryDownspouts', label: '1-Story Downspout Count' },
    { group: 'Work Details', value: 'twoStoryDownspouts', label: '2-Story Downspout Count' },
    { group: 'Work Details', value: 'materialPickupMARS', label: 'Material Pickup: MARS' },
    { group: 'Work Details', value: 'materialPickupSRS', label: 'Material Pickup: SRS' },
    { group: 'Work Details', value: 'materialPickupABC', label: 'Material Pickup: ABC' },
    { group: 'Work Details', value: 'siding', label: 'Siding' },
    { group: 'Work Details', value: 'windowScreens', label: 'Window Screens' },
    { group: 'Work Details', value: 'windowTrim', label: 'Window Trim' },
    { group: 'Work Details', value: 'standingSeam', label: 'Standing Seam' },
    { group: 'Work Details', value: 'gutterRepair', label: 'Gutter Repair' },
    { group: 'Work Details', value: 'chaseCover', label: 'Chase Cover' },
    { group: 'Work Details', value: 'interior', label: 'Interior' },
    { group: 'Work Details', value: 'flatRoof', label: 'Flat Roof (EPDM/TPO/ModBit/None)' },

    { group: 'Job Status / Flags', value: 'newStatus', label: 'New Status (on status change)' },
    { group: 'Job Status / Flags', value: 'signatureStatus', label: 'Signature Status' },
    { group: 'Job Status / Flags', value: 'invoiceStatus', label: 'Invoice Status (None/Open/Closed)' },
    { group: 'Job Status / Flags', value: 'priorityCustomer', label: 'Priority Customer' },
    { group: 'Job Status / Flags', value: 'workOrderStatus', label: 'Work Order Status (WO trigger)' },
    { group: 'Job Status / Flags', value: 'contactLeadSource', label: 'Contact Lead Source (contact trigger)' },

    { group: 'Property Management', value: 'tenantName', label: 'Tenant Name' },
    { group: 'Property Management', value: 'tenantContact', label: 'Tenant Contact' },
    { group: 'Property Management', value: 'workOrderType', label: 'Work Order Type' },
    { group: 'Property Management', value: 'workOrderText', label: 'Work Order Text' },

    { group: 'Notes / Outcome', value: 'repairDescription', label: 'Repair Description' },
    { group: 'Notes / Outcome', value: 'repairCompletionNotes', label: 'Repair Completion Notes' },
    { group: 'Notes / Outcome', value: 'notRoofRelatedNotes', label: 'Not-Roof-Related Notes' },
    { group: 'Notes / Outcome', value: 'overviewReportNotes', label: 'Overview Report Notes' },

    { group: 'Customer', value: 'customerType', label: 'Customer Type' },
    { group: 'Customer', value: 'customerCompanyName', label: 'Company Name' },
    { group: 'Customer', value: 'customerFirstName', label: 'Customer First Name' },
    { group: 'Customer', value: 'customerLastName', label: 'Customer Last Name' },
    { group: 'Customer', value: 'customerEmail', label: 'Customer Email' },
    { group: 'Customer', value: 'customerPhone', label: 'Customer Phone' },
    { group: 'Customer', value: 'propertyCity', label: 'Property City' },
    { group: 'Customer', value: 'propertyState', label: 'Property State' },
    { group: 'Customer', value: 'propertyZip', label: 'Property Zip' },
];

// Operators users can choose, with hints about what they do.
export const OPERATOR_OPTIONS = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'fuzzy', label: 'fuzzy match (~)' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'is_true', label: 'is true' },
    { value: 'is_false', label: 'is false' },
    { value: 'is_null', label: 'is empty' },
    { value: 'is_not_null', label: 'is not empty' },
];

// Operators that don't take a value (the right-hand side is implied by the op).
const VALUELESS_OPS = new Set(['is_true', 'is_false', 'is_null', 'is_not_null']);

// Fields whose right-hand side is one of a fixed set rather than free text.
// ConditionEvaluator compares against `SomeEnum.ToString()`, so the stored value
// must be the enum NAME — which is exactly what a typo in a hand-typed box gets
// wrong, silently, with the automation just never firing.
const enumOptions = (...names) => names.map((n) => ({ value: n, label: n }));

export const FIELD_VALUE_OPTIONS = {
    status: STATUS_KEY_SELECT_OPTIONS,
    newStatus: STATUS_KEY_SELECT_OPTIONS,
    type: enumOptions(
        'RepairLog', 'ResidentialInsurance', 'ResidentialRetail',
        'Commercial', 'PropertyManagement', 'PropertyManagementResidential'
    ),
    classification: enumOptions(...CLASSIFICATION_KEYS),
    flatRoof: enumOptions('None', 'EPDM', 'TPO', 'ModBit'),
    signatureStatus: enumOptions('None', 'Sent', 'PartiallySigned', 'FullySigned'),
    invoiceStatus: enumOptions('None', 'Open', 'Closed'),
    customerType: enumOptions(
        'Homeowner', 'PropertyManager', 'Subcontractor',
        'Supplier', 'Inspector', 'Adjuster', 'Other', 'Tenant'
    ),
    contactLeadSource: enumOptions(
        'Google', 'Referral', 'PropertyManagement', 'Warranty', 'Progress', 'Other', 'Spotio'
    ),
    // NOT enumOptions: Project.leadSource is free text and stores the human
    // label ("Property Management", with the space), unlike Customer.LeadSource
    // which is an int-backed enum stored by name. Same list, two spellings —
    // mixing them up is a rule that silently never matches.
    leadSource: LEAD_SOURCES.map((src) => ({ value: src.value, label: src.label })),
};

// Only the two exact-match operators get the dropdown. `contains` / `starts_with`
// / `fuzzy` on an enum are a partial-string match by design, so those keep the
// free-text box.
const EXACT_OPS = new Set(['equals', 'not_equals']);

export const blankGroup = () => ({ logic: 'AND', conditions: [], groups: [] });
const blankCondition = () => ({ field: 'status', operator: 'equals', value: '' });

// Recursive group component. Renders its own logic toggle + conditions + sub-groups,
// each editable in place. Bubbles changes up via onChange(newGroup).
const ConditionGroupNode = ({ group, onChange, onRemove, depth = 0 }) => {
    const update = (patch) => onChange({ ...group, ...patch });

    const setCondition = (idx, next) => {
        const conditions = group.conditions.map((c, i) => (i === idx ? next : c));
        update({ conditions });
    };
    const removeCondition = (idx) => {
        update({ conditions: group.conditions.filter((_, i) => i !== idx) });
    };
    const addCondition = () => {
        update({ conditions: [...group.conditions, blankCondition()] });
    };

    const setSubGroup = (idx, next) => {
        const groups = group.groups.map((g, i) => (i === idx ? next : g));
        update({ groups });
    };
    const removeSubGroup = (idx) => {
        update({ groups: group.groups.filter((_, i) => i !== idx) });
    };
    const addSubGroup = (logic) => {
        update({ groups: [...group.groups, { logic, conditions: [], groups: [] }] });
    };

    return (
        <div className={`ctb-group ctb-depth-${Math.min(depth, 3)}`}>
            <div className="ctb-group-header">
                <select
                    value={group.logic}
                    onChange={(e) => update({ logic: e.target.value })}
                    className="ctb-logic-select"
                    title="How to combine the conditions and sub-groups inside this group"
                >
                    <option value="AND">Match ALL (AND)</option>
                    <option value="OR">Match ANY (OR)</option>
                </select>
                {depth > 0 && onRemove && (
                    <button type="button" className="ctb-remove-btn" onClick={onRemove} title="Remove this group">
                        ×
                    </button>
                )}
            </div>

            {group.conditions.length === 0 && group.groups.length === 0 && (
                <div className="ctb-empty-hint">No conditions yet — add one below.</div>
            )}

            {group.conditions.map((c, idx) => (
                <ConditionRow
                    key={idx}
                    condition={c}
                    onChange={(next) => setCondition(idx, next)}
                    onRemove={() => removeCondition(idx)}
                />
            ))}

            {group.groups.map((g, idx) => (
                <ConditionGroupNode
                    key={idx}
                    group={g}
                    onChange={(next) => setSubGroup(idx, next)}
                    onRemove={() => removeSubGroup(idx)}
                    depth={depth + 1}
                />
            ))}

            <div className="ctb-add-row">
                <button type="button" className="ctb-add-btn" onClick={addCondition}>
                    + Condition
                </button>
                <button type="button" className="ctb-add-btn" onClick={() => addSubGroup('AND')}>
                    + AND group
                </button>
                <button type="button" className="ctb-add-btn" onClick={() => addSubGroup('OR')}>
                    + OR group
                </button>
            </div>
        </div>
    );
};

const ConditionRow = ({ condition, onChange, onRemove }) => {
    const isValueless = VALUELESS_OPS.has(condition.operator);
    const fieldOptions = FIELD_VALUE_OPTIONS[condition.field];
    const useDropdown = !!fieldOptions && EXACT_OPS.has(condition.operator);

    // An automation saved earlier (or imported) can hold a value that isn't in the
    // list — a renamed stage, or something typed before this field had options.
    // Keep it as an option so opening the automation doesn't silently rewrite it
    // to whatever happens to be first.
    const valueOptions = useDropdown && condition.value && !fieldOptions.some((o) => o.value === condition.value)
        ? [...fieldOptions, { value: condition.value, label: `${condition.value} (unrecognized)` }]
        : fieldOptions;

    return (
        <div className="ctb-condition-row">
            <select
                value={condition.field}
                onChange={(e) => {
                    // A value from the old field is meaningless against the new one
                    // ("Lead" left behind on Project Name), so drop it unless the new
                    // field actually accepts it.
                    const field = e.target.value;
                    const allowed = FIELD_VALUE_OPTIONS[field];
                    const keepValue = !allowed || allowed.some((o) => o.value === condition.value);
                    onChange({ ...condition, field, value: keepValue ? condition.value : '' });
                }}
                className="ctb-field-select"
            >
                {Array.from(new Set(FIELD_OPTIONS.map((f) => f.group))).map((g) => (
                    <optgroup key={g} label={g}>
                        {FIELD_OPTIONS.filter((f) => f.group === g).map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </optgroup>
                ))}
            </select>

            <select
                value={condition.operator}
                onChange={(e) => onChange({ ...condition, operator: e.target.value })}
                className="ctb-op-select"
            >
                {OPERATOR_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>

            {!isValueless && (
                useDropdown ? (
                    <select
                        value={condition.value ?? ''}
                        onChange={(e) => onChange({ ...condition, value: e.target.value })}
                        className="ctb-value-input"
                    >
                        <option value="">— pick a value —</option>
                        {valueOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        value={condition.value ?? ''}
                        onChange={(e) => onChange({ ...condition, value: e.target.value })}
                        placeholder="value"
                        className="ctb-value-input"
                    />
                )
            )}

            <button type="button" className="ctb-remove-btn" onClick={onRemove} title="Remove condition">
                ×
            </button>
        </div>
    );
};

// Top-level builder — owns the root group and renders the recursive tree.
const ConditionTreeBuilder = ({ value, onChange }) => {
    const root = value ?? blankGroup();
    return <ConditionGroupNode group={root} onChange={onChange} depth={0} />;
};

export default ConditionTreeBuilder;
